// KtMasteryDashboard.js — Teacher-facing mastery heatmap (futuristic neon)
// Neon glow cells, pulse animations, crosshair hover, glassmorphism cards.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Modal,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import { getMainContentColors } from "./MainContentArea";
import { KT_THRESHOLD_STRUGGLING, KT_THRESHOLD_PROFICIENT } from "../constants/kt";

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const CELL_W = 64;
const CELL_H = 38;
const NAME_COL_W = 180;
const HEADER_H = 48;
const RADIUS = { card: 10, cell: 4, pill: 12, modal: 14 };
const THRESHOLDS = { STRUGGLING: KT_THRESHOLD_STRUGGLING, DEVELOPING: KT_THRESHOLD_PROFICIENT };

const MONO = Platform.OS === "web"
  ? 'ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, Consolas, monospace'
  : undefined;

// Neon palette with glow properties
const NEON = {
  dark: {
    struggling: {
      bg: "rgba(255,50,50,0.06)", border: "rgba(255,50,50,0.18)",
      glow: "rgba(255,50,50,0.35)", glowFar: "rgba(255,50,50,0.12)",
      text: "#ff7070", textBright: "#ff9090", accent: "#ff3232",
    },
    developing: {
      bg: "rgba(255,180,30,0.06)", border: "rgba(255,180,30,0.18)",
      glow: "rgba(255,180,30,0.3)", glowFar: "rgba(255,180,30,0.1)",
      text: "#ffc040", textBright: "#ffd060", accent: "#ffb41e",
    },
    proficient: {
      bg: "rgba(0,232,157,0.05)", border: "rgba(0,232,157,0.15)",
      glow: "rgba(0,232,157,0.3)", glowFar: "rgba(0,232,157,0.1)",
      text: "#40e8b0", textBright: "#70ffd0", accent: "#00e89d",
    },
    noData: {
      bg: "rgba(100,120,150,0.03)", border: "rgba(100,120,150,0.08)",
      glow: "rgba(100,120,150,0.06)", glowFar: "rgba(100,120,150,0.02)",
      text: "#3a4558", textBright: "#5a6578", accent: "#4a5568",
    },
  },
  light: {
    struggling: {
      bg: "#fff5f5", border: "rgba(239,68,68,0.18)",
      glow: "rgba(239,68,68,0.2)", glowFar: "rgba(239,68,68,0.08)",
      text: "#dc2626", textBright: "#b91c1c", accent: "#ef4444",
    },
    developing: {
      bg: "#fffbeb", border: "rgba(245,158,11,0.18)",
      glow: "rgba(245,158,11,0.18)", glowFar: "rgba(245,158,11,0.06)",
      text: "#b45309", textBright: "#92400e", accent: "#f59e0b",
    },
    proficient: {
      bg: "#ecfdf5", border: "rgba(16,185,129,0.18)",
      glow: "rgba(16,185,129,0.18)", glowFar: "rgba(16,185,129,0.06)",
      text: "#047857", textBright: "#065f46", accent: "#10b981",
    },
    noData: {
      bg: "#f9fafb", border: "rgba(148,163,184,0.12)",
      glow: "rgba(148,163,184,0.06)", glowFar: "rgba(148,163,184,0.02)",
      text: "#94a3b8", textBright: "#64748b", accent: "#cbd5e1",
    },
  },
};

const STAT_ACCENTS = {
  students:   { light: "#3b82f6", dark: "#60a5fa" },
  mastery:    { light: "#8b5cf6", dark: "#a78bfa" },
  struggling: { light: "#ef4444", dark: "#ff4040" },
  proficient: { light: "#10b981", dark: "#00e89d" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const web = (st) => (Platform.OS === "web" ? st : {});

function getMasteryCategory(v) {
  if (v == null) return "noData";
  if (v < THRESHOLDS.STRUGGLING) return "struggling";
  if (v < THRESHOLDS.DEVELOPING) return "developing";
  return "proficient";
}

function getNeon(v, isDark) {
  return NEON[isDark ? "dark" : "light"][getMasteryCategory(v)];
}

function buildGlow(n, value, hovered) {
  const intensity = value != null ? value : 0;
  const base = Math.round(3 + intensity * 9);
  if (hovered) {
    return `0 0 ${base * 2}px ${n.glow}, 0 0 ${base * 4}px ${n.glowFar}, inset 0 0 ${base}px ${n.glowFar}`;
  }
  return `0 0 ${base}px ${n.glow}, 0 0 ${base * 2}px ${n.glowFar}`;
}

function fmtPct(v) {
  if (v == null) return "\u2014";
  return `${Math.round(v * 100)}%`;
}

function truncate(str, n) {
  if (!str) return "";
  return str.length > n ? str.slice(0, n - 1) + "\u2026" : str;
}

function csvEscape(val) {
  let s = String(val ?? "");
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}

function buildCsv(students, topics, matrix, names, topicNames) {
  const header = [csvEscape("Student"), ...topics.map((t) => csvEscape(topicNames?.[t] || `Topic ${t}`))].join(",");
  const rows = students.map((email, i) => {
    const name = (names && names[email]) || email;
    const cells = (matrix[i] || []).map((v) => csvEscape(v == null ? "N/A" : `${Math.round(v * 100)}%`));
    return [csvEscape(name), ...cells].join(",");
  });
  return [header, ...rows].join("\n");
}

function downloadCsv(csv, filename) {
  if (Platform.OS !== "web") return;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function computeTopicAverages(matrix, topicCount) {
  const sums = new Array(topicCount).fill(0);
  const counts = new Array(topicCount).fill(0);
  for (const row of matrix) {
    for (let c = 0; c < topicCount; c++) {
      const v = row ? row[c] : null;
      if (v != null) { sums[c] += v; counts[c]++; }
    }
  }
  return sums.map((sv, i) => (counts[i] > 0 ? sv / counts[i] : null));
}

function computeSummaryStats(students, matrix) {
  const studentAvgs = students.map((_, i) => {
    const vals = (matrix[i] || []).filter((v) => v != null);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  });
  const valid = studentAvgs.filter((v) => v != null);
  const overallAvg = valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
  return {
    total: students.length,
    avg: overallAvg,
    struggling: valid.filter((v) => v < THRESHOLDS.STRUGGLING).length,
    proficient: valid.filter((v) => v >= THRESHOLDS.DEVELOPING).length,
  };
}

// ---------------------------------------------------------------------------
// CSS keyframes injection (web only)
// ---------------------------------------------------------------------------
function useNeonKeyframes() {
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const id = "kt-neon-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes kt-pulse {
        0%, 100% { box-shadow: 0 0 4px rgba(255,50,50,0.2), 0 0 8px rgba(255,50,50,0.08); }
        50% { box-shadow: 0 0 14px rgba(255,50,50,0.5), 0 0 28px rgba(255,50,50,0.15), inset 0 0 6px rgba(255,50,50,0.1); }
      }
      @keyframes kt-skeleton {
        0%, 100% { opacity: 0.15; }
        50% { opacity: 0.35; }
      }
    `;
    document.head.appendChild(style);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Glassmorphism stat card */
const StatCard = React.memo(({ icon, label, value, accent, isDarkMode }) => {
  const colors = getMainContentColors(isDarkMode);
  return (
    <View style={[st.statCard, {
      backgroundColor: isDarkMode ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.7)",
      borderColor: isDarkMode ? `${accent}20` : "rgba(0,0,0,0.06)",
      ...web({
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: isDarkMode ? `0 0 20px ${accent}10` : "0 1px 3px rgba(0,0,0,0.06)",
      }),
    }]}>
      <View style={[st.statIconWrap, { backgroundColor: `${accent}18` }]}>
        <MaterialCommunityIcons name={icon} size={18} color={accent} />
      </View>
      <View>
        <Text style={[st.statValue, { color: isDarkMode ? accent : colors.textPrimary },
          web({ fontFamily: MONO, fontVariantNumeric: "tabular-nums", textShadow: isDarkMode ? `0 0 10px ${accent}40` : "none" })]}>
          {value}
        </Text>
        <Text style={[st.statLabel, { color: colors.textMuted }]}>{label}</Text>
      </View>
    </View>
  );
});

/** Class selector dropdown */
const ClassSelector = ({ classes, activeClassId, onSelect, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  const colors = getMainContentColors(isDarkMode);
  if (!classes || classes.length <= 1) return null;
  const current = classes.find((c) => (c.class_id ?? c.id) === activeClassId);
  const label = current?.class_name || current?.name || "Select Class";

  return (
    <View style={{ position: "relative", zIndex: 20 }}>
      <TouchableOpacity
        onPress={() => setOpen(!open)}
        style={[st.classBtn, {
          backgroundColor: isDarkMode ? "rgba(99,102,241,0.1)" : "#eef2ff",
          borderColor: isDarkMode ? "rgba(99,102,241,0.25)" : "#c7d2fe",
        }]}
      >
        <MaterialCommunityIcons name="school" size={14} color={colors.accentPrimary} />
        <Text style={[st.classBtnText, { color: colors.accentPrimary }]} numberOfLines={1}>{label}</Text>
        <MaterialCommunityIcons name={open ? "chevron-up" : "chevron-down"} size={14} color={colors.accentPrimary} />
      </TouchableOpacity>
      {open && (
        <>
          <TouchableOpacity
            style={[StyleSheet.absoluteFill, web({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 15 })]}
            onPress={() => setOpen(false)} activeOpacity={1}
          />
          <View style={[st.classDropdown, {
            backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
            borderColor: colors.border,
            ...web({ boxShadow: isDarkMode ? "0 4px 24px rgba(0,0,0,0.4)" : "0 4px 16px rgba(0,0,0,0.12)" }),
          }]}>
            {classes.map((cls) => {
              const id = cls.class_id ?? cls.id;
              const isActive = id === activeClassId;
              return (
                <TouchableOpacity key={id} onPress={() => { onSelect(id); setOpen(false); }}
                  style={[st.classItem, { backgroundColor: isActive ? (isDarkMode ? "rgba(99,102,241,0.12)" : "#eef2ff") : "transparent" }]}>
                  <Text style={[st.classItemText, { color: isActive ? colors.accentPrimary : colors.textPrimary, fontWeight: isActive ? "600" : "400" }]}>
                    {cls.class_name || cls.name}
                  </Text>
                  {isActive && <MaterialCommunityIcons name="check" size={14} color={colors.accentPrimary} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
};

/** Neon glow heatmap cell — color-only, no percentage text */
const HeatmapCell = React.memo(({ value, isDarkMode, studentEmail, studentName, topicId, topicName, onCellClick, accessibilityLabel, width, height }) => {
  const [hovered, setHovered] = useState(false);
  const category = getMasteryCategory(value);
  const n = getNeon(value, isDarkMode);

  const handlePress = useCallback(() => {
    if (onCellClick) onCellClick(studentEmail, studentName, topicId, topicName);
  }, [onCellClick, studentEmail, studentName, topicId, topicName]);

  const webHover = Platform.OS === "web"
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={[{
        width, height,
        backgroundColor: value != null ? n.bg : (isDarkMode ? "rgba(100,120,150,0.03)" : "#f9fafb"),
        borderRadius: RADIUS.cell,
        margin: 1,
        justifyContent: "center",
        alignItems: "center",
        borderWidth: 1,
        borderColor: hovered ? n.accent : n.border,
      }, web({
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
        transform: hovered ? "scale(1.12)" : "scale(1)",
        boxShadow: value != null ? buildGlow(n, value, hovered) : "none",
        zIndex: hovered ? 10 : 1,
        ...(category === "struggling" && !hovered ? { animation: "kt-pulse 2.5s ease-in-out infinite" } : {}),
      })]}
      {...webHover}
    >
      {value == null && (
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: n.accent, opacity: 0.3 }} />
      )}
    </TouchableOpacity>
  );
});

/** Legend with neon dots */
const HeatmapLegend = ({ isDarkMode }) => {
  const colors = getMainContentColors(isDarkMode);
  const mode = isDarkMode ? "dark" : "light";
  const items = [
    { accent: NEON[mode].struggling.accent, glow: NEON[mode].struggling.glow, label: "Struggling (<30%)" },
    { accent: NEON[mode].developing.accent, glow: NEON[mode].developing.glow, label: "Developing (30\u201359%)" },
    { accent: NEON[mode].proficient.accent, glow: NEON[mode].proficient.glow, label: "Proficient (60%+)" },
    { accent: NEON[mode].noData.accent, glow: NEON[mode].noData.glow, label: "No data" },
  ];
  return (
    <View style={st.legendRow}>
      {items.map((it) => (
        <View key={it.label} style={[st.legendPill, {
          backgroundColor: isDarkMode ? "rgba(148,163,184,0.06)" : "#f8fafc",
          borderColor: isDarkMode ? "rgba(148,163,184,0.12)" : "#e2e8f0",
        }]}>
          <View style={[st.legendDot, { backgroundColor: it.accent },
            web({ boxShadow: isDarkMode ? `0 0 6px ${it.glow}` : "none" })]} />
          <Text style={[st.legendText, { color: colors.textSecondary }, web({ fontFamily: MONO })]}>{it.label}</Text>
        </View>
      ))}
    </View>
  );
};

/** Class average summary row — color-only cells */
const SummaryStatsRow = ({ averages, cellWidth, cellHeight, isDarkMode }) => {
  const colors = getMainContentColors(isDarkMode);
  return (
    <View style={[st.matrixRow, { minHeight: cellHeight + 2 }]}>
      <View style={[st.nameCell, {
        width: NAME_COL_W,
        backgroundColor: isDarkMode ? "#0f172a" : "#ffffff",
        borderRightColor: colors.border,
      }, web({ position: "sticky", left: 0, zIndex: 3 })]}>
        <Text style={[st.nameText, { color: colors.accentPrimary, fontWeight: "700", fontStyle: "italic", fontSize: 11 },
          web({ fontFamily: MONO })]}>
          CLASS AVG
        </Text>
      </View>
      {averages.map((avg, i) => {
        const n = getNeon(avg, isDarkMode);
        return (
          <View key={`avg-${i}`} style={[{
            width: cellWidth, height: cellHeight,
            backgroundColor: n.bg, borderRadius: RADIUS.cell,
            margin: 1, justifyContent: "center", alignItems: "center",
            borderWidth: 1, borderColor: n.border,
          }, web({
            boxShadow: avg != null ? `0 0 ${Math.round(3 + (avg || 0) * 9)}px ${n.glow}` : "none",
          })]}>
            {avg == null && (
              <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: n.accent, opacity: 0.3 }} />
            )}
          </View>
        );
      })}
    </View>
  );
};

/** Drill-down modal with glass effect */
const DrillDownModal = ({ visible, onClose, title, children, isDarkMode }) => {
  const colors = getMainContentColors(isDarkMode);
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[st.modalOverlay, web({ backdropFilter: "blur(8px)" })]}>
        <View style={[st.modalCard, {
          backgroundColor: isDarkMode ? "rgba(15,23,42,0.92)" : colors.cardBg,
          borderColor: isDarkMode ? "rgba(129,140,248,0.15)" : colors.border,
          ...web({ boxShadow: isDarkMode ? "0 8px 40px rgba(0,0,0,0.5), 0 0 30px rgba(129,140,248,0.06)" : "0 8px 32px rgba(0,0,0,0.18)" }),
        }]}>
          <View style={[st.modalHeader, { borderBottomColor: isDarkMode ? "rgba(129,140,248,0.1)" : colors.border }]}>
            <Text style={[st.modalTitle, { color: colors.textPrimary }]} numberOfLines={1}>{title}</Text>
            <TouchableOpacity onPress={onClose} style={[st.modalCloseBtn, {
              backgroundColor: isDarkMode ? "rgba(148,163,184,0.1)" : "#f1f5f9",
            }]}>
              <MaterialCommunityIcons name="close" size={18} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <ScrollView style={st.modalBody} showsVerticalScrollIndicator={false}>{children}</ScrollView>
        </View>
      </View>
    </Modal>
  );
};

/** Neon mastery bar for drill-down — color bar, no percentage */
const MasteryBar = ({ value, label, sublabel, isDarkMode }) => {
  const colors = getMainContentColors(isDarkMode);
  const n = getNeon(value, isDarkMode);
  const pct = value != null ? Math.round(value * 100) : 0;
  return (
    <View style={[st.masteryBarRow, { borderBottomColor: isDarkMode ? "rgba(148,163,184,0.08)" : colors.border }]}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <View style={[{ width: 8, height: 8, borderRadius: 4, backgroundColor: n.accent },
          web({ boxShadow: isDarkMode ? `0 0 6px ${n.glow}` : "none" })]} />
        <View style={{ flex: 1 }}>
          <Text style={[st.masteryBarLabel, { color: colors.textPrimary }]}>{label}</Text>
          {sublabel ? <Text style={[st.masteryBarSub, { color: colors.textMuted }]}>{sublabel}</Text> : null}
        </View>
      </View>
      <View style={st.masteryBarTrack}>
        <View style={[st.masteryBarTrackBg, { backgroundColor: isDarkMode ? "rgba(148,163,184,0.06)" : "#f1f5f9" }]}>
          <View style={[st.masteryBarFill, { width: `${pct}%`, backgroundColor: n.accent },
            web({ transition: "width 0.4s ease", boxShadow: isDarkMode ? `0 0 8px ${n.glow}` : "none" })]} />
        </View>
      </View>
    </View>
  );
};

/** Response card for cell drill-down — mini quiz review */
const ResponseCard = React.memo(({ response, isDarkMode, index }) => {
  const colors = getMainContentColors(isDarkMode);
  const correct = response.is_correct;
  const n = correct
    ? NEON[isDarkMode ? "dark" : "light"].proficient
    : NEON[isDarkMode ? "dark" : "light"].struggling;

  const options = response.options || {};
  const optionKeys = Object.keys(options).filter((k) => k.startsWith("option")).sort();
  const correctAnswer = response.correct_answer;
  const selectedAnswer = response.selected_answer;

  // Normalize for comparison: "option2" -> "option2", "2" -> "option2"
  const normalizeKey = (val) => {
    if (!val) return "";
    const v = String(val).trim().toLowerCase();
    return v.startsWith("option") ? v : `option${v}`;
  };
  const selectedKey = normalizeKey(selectedAnswer);
  const correctKey = normalizeKey(correctAnswer);

  const timeStr = response.answered_at
    ? new Date(response.answered_at).toLocaleDateString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <View style={[{
      marginBottom: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: correct
        ? (isDarkMode ? "rgba(0,232,157,0.15)" : "rgba(16,185,129,0.15)")
        : (isDarkMode ? "rgba(255,50,50,0.15)" : "rgba(239,68,68,0.12)"),
      backgroundColor: correct
        ? (isDarkMode ? "rgba(0,232,157,0.03)" : "rgba(16,185,129,0.03)")
        : (isDarkMode ? "rgba(255,50,50,0.03)" : "rgba(239,68,68,0.02)"),
      overflow: "hidden",
    }, web({
      transition: "border-color 0.2s ease",
    })]}>
      {/* Header bar */}
      <View style={{
        flexDirection: "row", alignItems: "center", justifyContent: "space-between",
        paddingHorizontal: 14, paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: isDarkMode ? "rgba(148,163,184,0.06)" : "rgba(0,0,0,0.04)",
      }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={[{
            width: 22, height: 22, borderRadius: 11,
            justifyContent: "center", alignItems: "center",
            backgroundColor: correct
              ? (isDarkMode ? "rgba(0,232,157,0.15)" : "rgba(16,185,129,0.12)")
              : (isDarkMode ? "rgba(255,50,50,0.15)" : "rgba(239,68,68,0.12)"),
          }, web({
            boxShadow: isDarkMode ? `0 0 8px ${n.glow}` : "none",
          })]}>
            <MaterialCommunityIcons
              name={correct ? "check" : "close"}
              size={13}
              color={n.accent}
            />
          </View>
          <Text style={{ fontSize: 11, fontWeight: "600", color: n.text, letterSpacing: 0.3 }}>
            {correct ? "CORRECT" : "INCORRECT"}
          </Text>
        </View>
        {timeStr && (
          <Text style={[{ fontSize: 10, color: colors.textMuted, fontWeight: "500" }, web({ fontFamily: MONO })]}>
            {timeStr}
          </Text>
        )}
      </View>

      {/* Question text */}
      <View style={{ paddingHorizontal: 14, paddingTop: 10, paddingBottom: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: "600", lineHeight: 18, color: colors.textPrimary }}>
          {response.question_text}
        </Text>
      </View>

      {/* Options */}
      <View style={{ paddingHorizontal: 14, paddingBottom: 12 }}>
        {optionKeys.map((key) => {
          const isSelected = key === selectedKey;
          const isCorrectOption = key === correctKey;
          const showAsCorrect = isCorrectOption && !correct; // highlight correct when student got it wrong

          let optBg = "transparent";
          let optBorder = isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(0,0,0,0.05)";
          let optTextColor = colors.textSecondary;
          let icon = null;

          if (isSelected && correct) {
            optBg = isDarkMode ? "rgba(0,232,157,0.08)" : "rgba(16,185,129,0.06)";
            optBorder = isDarkMode ? "rgba(0,232,157,0.25)" : "rgba(16,185,129,0.2)";
            optTextColor = NEON[isDarkMode ? "dark" : "light"].proficient.text;
            icon = "check-circle";
          } else if (isSelected && !correct) {
            optBg = isDarkMode ? "rgba(255,50,50,0.08)" : "rgba(239,68,68,0.06)";
            optBorder = isDarkMode ? "rgba(255,50,50,0.25)" : "rgba(239,68,68,0.2)";
            optTextColor = NEON[isDarkMode ? "dark" : "light"].struggling.text;
            icon = "close-circle";
          } else if (showAsCorrect) {
            optBg = isDarkMode ? "rgba(0,232,157,0.05)" : "rgba(16,185,129,0.04)";
            optBorder = isDarkMode ? "rgba(0,232,157,0.15)" : "rgba(16,185,129,0.12)";
            optTextColor = NEON[isDarkMode ? "dark" : "light"].proficient.text;
            icon = "check-circle-outline";
          }

          const label = key.replace("option", "");

          return (
            <View key={key} style={[{
              flexDirection: "row", alignItems: "center",
              paddingVertical: 6, paddingHorizontal: 10,
              marginTop: 4, borderRadius: 6,
              borderWidth: 1,
              backgroundColor: optBg, borderColor: optBorder,
            }, web({ transition: "all 0.15s ease" })]}>
              <Text style={[{
                width: 20, fontSize: 10, fontWeight: "700", color: colors.textMuted,
                textAlign: "center", marginRight: 8,
              }, web({ fontFamily: MONO })]}>
                {label}
              </Text>
              <Text style={{ flex: 1, fontSize: 12, fontWeight: isSelected ? "600" : "400", color: optTextColor, lineHeight: 16 }}>
                {options[key]}
              </Text>
              {icon && (
                <MaterialCommunityIcons
                  name={icon}
                  size={16}
                  color={isSelected && !correct
                    ? NEON[isDarkMode ? "dark" : "light"].struggling.accent
                    : NEON[isDarkMode ? "dark" : "light"].proficient.accent}
                  style={{ marginLeft: 6 }}
                />
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
});

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const KtMasteryDashboard = ({
  classId,
  collectionId = null,
  isDarkMode = false,
  apiBaseUrl,
  authToken,
}) => {
  const { width: winW } = useWindowDimensions();
  const colors = getMainContentColors(isDarkMode);
  useNeonKeyframes();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masteryData, setMasteryData] = useState(null);
  // collectionId prop used directly — no mirrored state to avoid double-fetch race
  const [studentModal, setStudentModal] = useState(null);
  const [topicModal, setTopicModal] = useState(null);
  const [studentHistory, setStudentHistory] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);
  const [cellModal, setCellModal] = useState(null); // { email, name, topicId, topicName }
  const [cellResponses, setCellResponses] = useState(null);
  const [cellLoading, setCellLoading] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false); const [classes, setClasses] = useState([]);
  const [activeClassId, setActiveClassId] = useState(classId);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showAdjusted, setShowAdjusted] = useState(true);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  // Fetch classes if no classId prop
  useEffect(() => {
    if (classId) { setActiveClassId(classId); return; }
    if (!apiBaseUrl || !authToken) return;
    (async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/classes/mine`, { headers: authHeaders });
        const list = res.data?.classes || res.data || [];
        setClasses(list);
        if (list.length > 0) {
          setActiveClassId(list[0].class_id ?? list[0].id);
        } else {
          setLoading(false);
          setError("No classes found. Create a class first to view mastery data.");
        }
      } catch (err) {
        console.error("KtMasteryDashboard: failed to fetch classes", err);
        setLoading(false);
        setError("Failed to load classes.");
      }
    })();
  }, [classId, apiBaseUrl, authToken]);

  const fetchMasteryData = useCallback(async () => {
    if (!activeClassId || !apiBaseUrl) return;
    setLoading(true);
    setError(null);
    try {
      const params = collectionId ? { collection_id: collectionId } : {};
      const res = await axios.get(
        `${apiBaseUrl}/kt/teacher/class/${activeClassId}/mastery`,
        { headers: authHeaders, params }
      );
      setMasteryData(res.data);
    } catch (err) {
      console.error("KtMasteryDashboard: failed to fetch mastery data", err);
      setError(err.response?.status === 404
        ? "No mastery data available for this class yet."
        : "Failed to load mastery data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [activeClassId, apiBaseUrl, authHeaders, collectionId]);

  useEffect(() => { fetchMasteryData(); }, [fetchMasteryData]);

  const handleStudentClick = useCallback(async (email, name) => {
    setStudentModal({ email, name });
    setStudentHistory(null);
    setStudentLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/kt/teacher/student/${encodeURIComponent(email)}/history`,
        { headers: authHeaders, params: { class_id: activeClassId } }
      );
      setStudentHistory(res.data);
    } catch (err) {
      console.error("Failed to fetch student history:", err);
      setStudentHistory({ error: "Failed to load student history." });
    } finally {
      setStudentLoading(false);
    }
  }, [apiBaseUrl, authHeaders, activeClassId]);

  const handleTopicClick = useCallback(async (topic, index) => {
    setTopicModal({ topic, index });
    setTopicDetails(null);
    setTopicLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/kt/teacher/class/${activeClassId}/topic/${encodeURIComponent(topic)}/details`,
        { headers: authHeaders }
      );
      setTopicDetails(res.data);
    } catch (err) {
      console.error("Failed to fetch topic details:", err);
      setTopicDetails({ error: "Failed to load topic details." });
    } finally {
      setTopicLoading(false);
    }
  }, [apiBaseUrl, authHeaders, activeClassId]);

  const handleCellClick = useCallback(async (email, name, topicId, topicName) => {
    setCellModal({ email, name, topicId, topicName });
    setCellResponses(null);
    setCellLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/kt/teacher/student/${encodeURIComponent(email)}/topic/${encodeURIComponent(topicId)}/responses`,
        { headers: authHeaders, params: { class_id: activeClassId } }
      );
      setCellResponses(res.data);
    } catch (err) {
      console.error("Failed to fetch cell responses:", err);
      setCellResponses({ error: "Failed to load response data." });
    } finally {
      setCellLoading(false);
    }
  }, [apiBaseUrl, authHeaders, activeClassId]);

  const hasAdjusted = !!masteryData?.adjusted_mastery_matrix;

  const activeMatrix = useMemo(() => {
    if (!masteryData) return [];
    if (showAdjusted && hasAdjusted) return masteryData.adjusted_mastery_matrix;
    return masteryData.mastery_matrix;
  }, [masteryData, showAdjusted, hasAdjusted]);

  const handleExport = useCallback(() => {
    if (!masteryData) return;
    const csv = buildCsv(masteryData.students, masteryData.topics, activeMatrix, masteryData.student_names, masteryData.topic_names);
    downloadCsv(csv, `mastery_${activeClassId}_${Date.now()}.csv`);
  }, [masteryData, activeClassId, activeMatrix]);

  const sortedIdx = useMemo(() => {
    if (!masteryData) return [];
    const { students, student_names } = masteryData;
    return [...students.map((_, i) => i)].sort((a, b) => {
      const na = (student_names?.[students[a]] || students[a]).toLowerCase();
      const nb = (student_names?.[students[b]] || students[b]).toLowerCase();
      return na < nb ? -1 : na > nb ? 1 : 0;
    });
  }, [masteryData]);

  const topicAvgs = useMemo(() => {
    if (!masteryData) return [];
    return computeTopicAverages(activeMatrix, masteryData.topics.length);
  }, [masteryData, activeMatrix]);

  const summary = useMemo(() => {
    if (!masteryData) return null;
    return computeSummaryStats(masteryData.students, activeMatrix);
  }, [masteryData, activeMatrix]);

  const topicCount = masteryData?.topics?.length || 0;
  const availW = winW - NAME_COL_W - 80;
  const cellW = Math.max(CELL_W, topicCount > 0 ? Math.floor(availW / topicCount) : CELL_W);
  const cellH = CELL_H;

  // ---- Loading ----
  if (loading) {
    return (
      <View style={[st.centerWrap, { backgroundColor: colors.background }]}>
        <View style={web({ boxShadow: isDarkMode ? "0 0 40px rgba(129,140,248,0.12)" : "none" })}>
          <ActivityIndicator size="large" color={isDarkMode ? "#818cf8" : colors.accentPrimary} />
        </View>
        <Text style={[st.statusText, { color: colors.textSecondary, marginTop: 16 }, web({ fontFamily: MONO })]}>
          LOADING MASTERY DATA...
        </Text>
      </View>
    );
  }

  // ---- Error ----
  if (error) {
    return (
      <View style={[st.centerWrap, { backgroundColor: colors.background }]}>
        <View style={[st.errorCard, {
          backgroundColor: isDarkMode ? "rgba(255,50,50,0.06)" : "#fef2f2",
          borderColor: isDarkMode ? "rgba(255,50,50,0.2)" : "#fecaca",
          ...web({ boxShadow: isDarkMode ? "0 0 20px rgba(255,50,50,0.08)" : "none" }),
        }]}>
          <MaterialCommunityIcons name="alert-circle-outline" size={32} color={NEON.dark.struggling.accent} />
          <Text style={[st.statusText, { color: colors.textPrimary, marginTop: 12, textAlign: "center" }]}>{error}</Text>
          <TouchableOpacity style={[st.retryBtn, { backgroundColor: colors.accentPrimary }]} onPress={fetchMasteryData}>
            <MaterialCommunityIcons name="refresh" size={16} color="#fff" />
            <Text style={st.retryText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // ---- Empty ----
  if (!masteryData || !masteryData.students?.length || !masteryData.topics?.length) {
    return (
      <View style={[st.centerWrap, { backgroundColor: colors.background }]}>
        <View style={{ opacity: 0.5 }}>
          <MaterialCommunityIcons name="chart-box-plus-outline" size={56} color={colors.textMuted} />
        </View>
        <Text style={[st.statusText, { color: colors.textPrimary, marginTop: 14, fontWeight: "600", fontSize: 15 },
          web({ fontFamily: MONO })]}>
          NO DATA YET
        </Text>
        <Text style={[st.statusText, { color: colors.textSecondary, marginTop: 6, textAlign: "center", maxWidth: 320 }]}>
          Students need to answer KT questions before mastery data appears here.
        </Text>
      </View>
    );
  }

  // ---- Dashboard ----
  const { students, topics, student_names, topic_names } = masteryData;
  const rowHighlight = isDarkMode ? "rgba(77,124,255,0.04)" : "rgba(59,130,246,0.03)";

  return (
    <View style={[st.root, { backgroundColor: colors.background }]}>
      {/* Toolbar */}
      <View style={[st.toolbar, { borderBottomColor: isDarkMode ? "rgba(129,140,248,0.1)" : colors.border }]}>
        <View style={st.toolbarLeft}>
          <View style={[st.toolbarIcon, { backgroundColor: isDarkMode ? "rgba(129,140,248,0.1)" : "#eef2ff" },
            web({ boxShadow: isDarkMode ? "0 0 12px rgba(129,140,248,0.15)" : "none" })]}>
            <MaterialCommunityIcons name="view-grid" size={18} color={isDarkMode ? "#818cf8" : colors.accentPrimary} />
          </View>
          <View>
            <Text style={[st.toolbarTitle, { color: colors.textPrimary }, web({ fontFamily: MONO })]}>
              MASTERY HEATMAP
            </Text>
            <Text style={[st.toolbarSub, { color: colors.textMuted }, web({ fontFamily: MONO })]}>
              {students.length} students {"\u00B7"} {topics.length} topics
            </Text>
          </View>
        </View>
        <View style={st.toolbarRight}>
          <View style={[st.togglePill, {
            backgroundColor: isDarkMode ? "rgba(129,140,248,0.08)" : "#f1f5f9",
            borderColor: isDarkMode ? "rgba(129,140,248,0.15)" : "#e2e8f0",
          }]}>
            <TouchableOpacity
              onPress={() => hasAdjusted && setShowAdjusted(true)}
              disabled={!hasAdjusted}
              style={[st.toggleOption, showAdjusted && hasAdjusted && {
                backgroundColor: isDarkMode ? "rgba(129,140,248,0.2)" : "#e0e7ff",
              }, !hasAdjusted && { opacity: 0.4 },
              web({ cursor: hasAdjusted ? "pointer" : "not-allowed", transition: "background-color 0.15s ease" })]}
              accessibilityRole="button"
              accessibilityLabel={hasAdjusted ? "Show adjusted mastery" : "Adjusted mastery unavailable"}
            >
              <Text style={[st.toggleText, {
                color: (showAdjusted && hasAdjusted)
                  ? (isDarkMode ? "#818cf8" : "#4f46e5")
                  : (isDarkMode ? "#64748b" : "#94a3b8"),
                fontWeight: (showAdjusted && hasAdjusted) ? "700" : "500",
              }, web({ fontFamily: MONO })]}>
                {hasAdjusted ? "Adjusted" : "Adjusted (N/A)"}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setShowAdjusted(false)}
              style={[st.toggleOption, !showAdjusted && {
                backgroundColor: isDarkMode ? "rgba(129,140,248,0.2)" : "#e0e7ff",
              }, web({ cursor: "pointer", transition: "background-color 0.15s ease" })]}
              accessibilityRole="button"
              accessibilityLabel="Show raw BKT mastery"
            >
              <Text style={[st.toggleText, {
                color: !showAdjusted
                  ? (isDarkMode ? "#818cf8" : "#4f46e5")
                  : (isDarkMode ? "#64748b" : "#94a3b8"),
                fontWeight: !showAdjusted ? "700" : "500",
              }, web({ fontFamily: MONO })]}>
                Raw
              </Text>
            </TouchableOpacity>
          </View>
          <ClassSelector classes={classes} activeClassId={activeClassId} onSelect={setActiveClassId} isDarkMode={isDarkMode} />
          {Platform.OS === "web" && (
            <TouchableOpacity
              style={[st.toolBtn, {
                backgroundColor: isDarkMode ? "rgba(99,102,241,0.08)" : "#eef2ff",
                borderColor: isDarkMode ? "rgba(99,102,241,0.2)" : "#c7d2fe",
              }]}
              onPress={handleExport}
            >
              <MaterialCommunityIcons name="download" size={15} color={colors.accentPrimary} />
              <Text style={[st.toolBtnText, { color: colors.accentPrimary }]}>Export CSV</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[st.iconBtn, { backgroundColor: isDarkMode ? "rgba(148,163,184,0.08)" : "#f1f5f9" }]} onPress={fetchMasteryData}>
            <MaterialCommunityIcons name="refresh" size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Stat Cards */}
      {summary && (
        <View style={st.statsRow}>
          <StatCard icon="account-group" label="Students" value={String(summary.total)}
            accent={STAT_ACCENTS.students[isDarkMode ? "dark" : "light"]} isDarkMode={isDarkMode} />
          <StatCard icon="alert-circle-outline" label="Need Support" value={String(summary.struggling)}
            accent={STAT_ACCENTS.struggling[isDarkMode ? "dark" : "light"]} isDarkMode={isDarkMode} />
          <StatCard icon="check-circle-outline" label="On Track" value={String(summary.proficient)}
            accent={STAT_ACCENTS.proficient[isDarkMode ? "dark" : "light"]} isDarkMode={isDarkMode} />
        </View>
      )}

      {/* Legend */}
      <HeatmapLegend isDarkMode={isDarkMode} />

      {/* Heatmap */}
      <ScrollView horizontal showsHorizontalScrollIndicator style={st.hScroll} contentContainerStyle={st.hScrollContent}>
        <View style={web({ background: isDarkMode ? "rgba(77,124,255,0.02)" : "transparent" })}>
          {/* Topic headers */}
          <View style={[st.matrixRow, st.headerRow, {
            backgroundColor: isDarkMode ? "rgba(30,41,59,0.6)" : "#f8fafc",
            borderBottomColor: isDarkMode ? "rgba(129,140,248,0.1)" : colors.border,
          }]}>
            <View style={[st.nameCell, st.cornerCell, {
              width: NAME_COL_W,
              backgroundColor: isDarkMode ? "rgba(30,41,59,0.8)" : "#f8fafc",
              borderRightColor: isDarkMode ? "rgba(129,140,248,0.08)" : colors.border,
            }, web({ position: "sticky", left: 0, zIndex: 4 })]}>
              <Text style={[st.cornerText, { color: colors.textMuted }, web({ fontFamily: MONO })]}>
                STUDENT / TOPIC
              </Text>
            </View>
            {topics.map((topic, ci) => {
              const topicLabel = topic_names?.[topic] || `Topic ${topic}`;
              return (
                <TouchableOpacity key={`th-${ci}`} style={[st.topicHeader, { width: cellW, minHeight: HEADER_H }]}
                  onPress={() => handleTopicClick(topic, ci)}
                  accessibilityRole="button"
                  accessibilityLabel={`View details for ${topicLabel}`}>
                  <Text style={[st.topicHeaderText, { color: colors.textPrimary }]} numberOfLines={2}>
                    {truncate(topicLabel, 22)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Student rows */}
          <ScrollView showsVerticalScrollIndicator style={st.vScroll}>
            {sortedIdx.map((ri) => {
              const email = students[ri];
              const name = student_names?.[email] || email;
              const row = activeMatrix[ri] || [];
              const isRowHovered = hoveredRow === ri;
              return (
                <View key={`row-${ri}`}
                  style={[st.matrixRow, {
                    borderBottomColor: isDarkMode ? "rgba(148,163,184,0.04)" : colors.border,
                    borderBottomWidth: StyleSheet.hairlineWidth,
                    backgroundColor: isRowHovered ? rowHighlight : "transparent",
                  }, web({ transition: "background-color 0.15s ease" })]}
                  {...(Platform.OS === "web" ? {
                    onMouseEnter: () => setHoveredRow(ri),
                    onMouseLeave: () => setHoveredRow(null),
                  } : {})}
                >
                  <TouchableOpacity
                    style={[st.nameCell, {
                      width: NAME_COL_W,
                      backgroundColor: isDarkMode
                        ? (isRowHovered ? "rgba(15,23,42,0.95)" : "#0f172a")
                        : (isRowHovered ? "#f8fafc" : "#ffffff"),
                      borderRightColor: isDarkMode ? "rgba(129,140,248,0.08)" : colors.border,
                      borderLeftWidth: isRowHovered ? 2 : 0,
                      borderLeftColor: isDarkMode ? "#818cf8" : "#3b82f6",
                    }, web({ position: "sticky", left: 0, zIndex: 3, transition: "all 0.15s ease" })]}
                    onPress={() => handleStudentClick(email, name)}
                    accessibilityRole="button"
                    accessibilityLabel={`View history for ${name}`}
                    accessibilityHint="Opens student mastery history"
                  >
                    <Text style={[st.nameText, {
                      color: isRowHovered ? (isDarkMode ? "#e2e8f0" : "#1e293b") : colors.textPrimary,
                    }]} numberOfLines={1}>
                      {truncate(name, 24)}
                    </Text>
                  </TouchableOpacity>
                  {topics.map((topic, ci) => {
                    const topicLabel = topic_names?.[topic] || `Topic ${topic}`;
                    const category = getMasteryCategory(row[ci]);
                    return (
                      <HeatmapCell key={`c-${ri}-${ci}`}
                        value={row[ci] != null ? row[ci] : null}
                        isDarkMode={isDarkMode} width={cellW} height={cellH}
                        studentEmail={email} studentName={name}
                        topicId={topic} topicName={topicLabel}
                        onCellClick={handleCellClick}
                        accessibilityLabel={`${name}, ${topicLabel}: ${category}`} />
                    );
                  })}
                </View>
              );
            })}

            <View style={[st.summaryDivider, { borderTopColor: isDarkMode ? "rgba(129,140,248,0.12)" : colors.border }]} />
            <SummaryStatsRow averages={topicAvgs} cellWidth={cellW} cellHeight={cellH} isDarkMode={isDarkMode} />
          </ScrollView>
        </View>
      </ScrollView>

      {/* Student Modal */}
      <DrillDownModal visible={!!studentModal}
        onClose={() => { setStudentModal(null); setStudentHistory(null); }}
        title={studentModal ? studentModal.name : ""} isDarkMode={isDarkMode}>
        {studentLoading ? (
          <ActivityIndicator size="small" color={colors.accentPrimary} style={{ marginTop: 24 }} />
        ) : studentHistory?.error ? (
          <Text style={[st.drillError, { color: NEON.dark.struggling.accent }]}>{studentHistory.error}</Text>
        ) : studentHistory ? (
          <View>
            {(studentHistory.mastery_summary || []).map((entry, idx) => {
              const topicLabel = topic_names?.[entry.source_topic_id] || `Topic ${entry.source_topic_id}`;
              const correctRate = entry.total_attempts > 0
                ? entry.correct_attempts / entry.total_attempts
                : null;
              return (
                <MasteryBar key={idx} value={entry.p_mastery} label={topicLabel}
                  sublabel={[
                    entry.total_attempts > 0 && `${entry.total_attempts} responses`,
                    correctRate != null && `${Math.round(correctRate * 100)}% correct`,
                  ].filter(Boolean).join(" \u00B7 ") || null}
                  isDarkMode={isDarkMode} />
              );
            })}
            {(!studentHistory.mastery_summary || studentHistory.mastery_summary.length === 0) && (
              <Text style={[st.drillEmpty, { color: colors.textMuted }]}>No response history available.</Text>
            )}
          </View>
        ) : null}
      </DrillDownModal>

      {/* Topic Modal */}
      <DrillDownModal visible={!!topicModal}
        onClose={() => { setTopicModal(null); setTopicDetails(null); }}
        title={topicModal ? `Topic: ${topic_names?.[topicModal.topic] || topicModal.topic}` : ""} isDarkMode={isDarkMode}>
        {topicLoading ? (
          <ActivityIndicator size="small" color={colors.accentPrimary} style={{ marginTop: 24 }} />
        ) : topicDetails?.error ? (
          <Text style={[st.drillError, { color: NEON.dark.struggling.accent }]}>{topicDetails.error}</Text>
        ) : topicDetails ? (
          <View>
            {topicDetails.class_average != null && (() => {
              const catLabel = getMasteryCategory(topicDetails.class_average);
              const catN = getNeon(topicDetails.class_average, isDarkMode);
              return (
                <View style={[st.topicBanner, {
                  backgroundColor: isDarkMode ? "rgba(129,140,248,0.06)" : "#f5f3ff",
                  borderColor: isDarkMode ? "rgba(129,140,248,0.15)" : "#ddd6fe",
                }]}>
                  <Text style={[st.topicBannerLabel, { color: colors.textSecondary }]}>Class Average</Text>
                  <View style={{ flexDirection: "row", alignItems: "center", marginTop: 8, gap: 10 }}>
                    <View style={[{ width: 14, height: 14, borderRadius: 7, backgroundColor: catN.accent },
                      web({ boxShadow: isDarkMode ? `0 0 8px ${catN.glow}` : "none" })]} />
                    <Text style={{ fontSize: 14, fontWeight: "600", color: catN.text, textTransform: "capitalize" }}>
                      {catLabel === "noData" ? "No data" : catLabel}
                    </Text>
                  </View>
                  <View style={[st.topicMiniBar, { backgroundColor: isDarkMode ? "rgba(148,163,184,0.08)" : "#e2e8f0", marginTop: 10 }]}>
                    <View style={[st.topicMiniFill, {
                      width: `${Math.round(topicDetails.class_average * 100)}%`,
                      backgroundColor: catN.accent,
                    }, web({ boxShadow: isDarkMode ? `0 0 8px ${catN.glow}` : "none" })]} />
                  </View>
                </View>
              );
            })()}
            {topicDetails.student_details?.length > 0 && (
              <View style={{ marginTop: 16 }}>
                <Text style={[st.missedHeader, { color: colors.textPrimary }]}>Student Performance</Text>
                {topicDetails.student_details.map((sd, si) => {
                  const sdN = getNeon(sd.p_mastery, isDarkMode);
                  return (
                    <View key={si} style={[st.missedItem, { flexDirection: "row", alignItems: "center", gap: 10, borderBottomColor: isDarkMode ? "rgba(148,163,184,0.06)" : colors.border }]}>
                      <View style={[{ width: 10, height: 10, borderRadius: 5, backgroundColor: sdN.accent },
                        web({ boxShadow: isDarkMode ? `0 0 6px ${sdN.glow}` : "none" })]} />
                      <Text style={[st.missedQ, { flex: 1, color: colors.textPrimary }]}>{sd.name}</Text>
                      <Text style={[st.missedMeta, { color: colors.textSecondary }]}>
                        {sd.total_attempts > 0 ? `${sd.correct_attempts}/${sd.total_attempts} correct` : "No attempts"}
                      </Text>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        ) : null}
      </DrillDownModal>

      {/* Cell Drill-Down Modal — question-by-question review */}
      <DrillDownModal visible={!!cellModal}
        onClose={() => { setCellModal(null); setCellResponses(null); }}
        title={cellModal ? `${cellModal.name}` : ""} isDarkMode={isDarkMode}>
        {cellLoading ? (
          <ActivityIndicator size="small" color={colors.accentPrimary} style={{ marginTop: 24 }} />
        ) : cellResponses?.error ? (
          <Text style={[st.drillError, { color: NEON.dark.struggling.accent }]}>{cellResponses.error}</Text>
        ) : cellResponses ? (
          <View>
            {/* Topic + mastery summary header */}
            <View style={[{
              flexDirection: "row", alignItems: "center", justifyContent: "space-between",
              paddingBottom: 14, marginBottom: 14,
              borderBottomWidth: 1,
              borderBottomColor: isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(0,0,0,0.06)",
            }]}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1, marginRight: 12 }}>
                <View style={[{
                  width: 12, height: 12, borderRadius: 6,
                  backgroundColor: getNeon(cellResponses.p_mastery, isDarkMode).accent,
                }, web({ boxShadow: isDarkMode ? `0 0 8px ${getNeon(cellResponses.p_mastery, isDarkMode).glow}` : "none" })]} />
                <Text style={{ fontSize: 13, fontWeight: "600", color: colors.textPrimary }} numberOfLines={1}>
                  {cellResponses.topic_name || cellModal?.topicName || cellModal?.topicId}
                </Text>
              </View>
              {cellResponses.total_attempts > 0 && (
                <View style={[{
                  flexDirection: "row", alignItems: "center", gap: 6,
                  paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8,
                  backgroundColor: isDarkMode ? "rgba(148,163,184,0.06)" : "#f1f5f9",
                  borderWidth: 1,
                  borderColor: isDarkMode ? "rgba(148,163,184,0.1)" : "#e2e8f0",
                }]}>
                  <MaterialCommunityIcons name="check-all" size={14}
                    color={getNeon(cellResponses.correct_attempts / Math.max(cellResponses.total_attempts, 1), isDarkMode).accent} />
                  <Text style={[{ fontSize: 12, fontWeight: "700", color: colors.textPrimary },
                    web({ fontFamily: MONO, fontVariantNumeric: "tabular-nums" })]}>
                    {cellResponses.correct_attempts} of {cellResponses.total_attempts} correct
                  </Text>
                </View>
              )}
            </View>

            {/* Response cards */}
            {(cellResponses.responses || []).map((resp, idx) => (
              <ResponseCard key={resp.response_id || idx} response={resp} isDarkMode={isDarkMode} index={idx} />
            ))}
            {(!cellResponses.responses || cellResponses.responses.length === 0) && (
              <Text style={[st.drillEmpty, { color: colors.textMuted }]}>No responses recorded for this topic yet.</Text>
            )}
          </View>
        ) : null}
      </DrillDownModal>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const st = StyleSheet.create({
  root: { flex: 1, borderRadius: 12 },
  centerWrap: { flex: 1, justifyContent: "center", alignItems: "center", padding: 32, minHeight: 240 },
  statusText: { fontSize: 14, fontWeight: "500", lineHeight: 20, letterSpacing: 0.5 },

  errorCard: { alignItems: "center", padding: 28, borderRadius: RADIUS.card, borderWidth: 1, maxWidth: 400 },
  retryBtn: { flexDirection: "row", alignItems: "center", gap: 6, marginTop: 18, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  retryText: { color: "#fff", fontWeight: "600", fontSize: 13 },

  toolbar: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, zIndex: 100 },
  toolbarLeft: { flexDirection: "row", alignItems: "center", gap: 12 },
  toolbarIcon: { width: 34, height: 34, borderRadius: 8, justifyContent: "center", alignItems: "center" },
  toolbarTitle: { fontSize: 14, fontWeight: "700", letterSpacing: 1.5 },
  toolbarSub: { fontSize: 11, fontWeight: "500", marginTop: 2, letterSpacing: 0.5 },
  toolbarRight: { flexDirection: "row", alignItems: "center", gap: 8, zIndex: 20 },

  toolBtn: { flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1 },
  toolBtnText: { fontSize: 13, fontWeight: "600" },
  iconBtn: { width: 34, height: 34, borderRadius: 8, justifyContent: "center", alignItems: "center" },

  classBtn: { flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1, maxWidth: 200 },
  classBtnText: { fontSize: 13, fontWeight: "600", flexShrink: 1 },
  classDropdown: { position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 200, borderRadius: 10, borderWidth: 1, overflow: "hidden", zIndex: 30 },
  classItem: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 14, paddingVertical: 10 },
  classItemText: { fontSize: 13 },

  statsRow: { flexDirection: "row", paddingHorizontal: 20, paddingTop: 16, paddingBottom: 4, gap: 12, flexWrap: "wrap" },
  statCard: { flex: 1, minWidth: 140, flexDirection: "row", alignItems: "center", gap: 12, paddingHorizontal: 14, paddingVertical: 14, borderRadius: RADIUS.card, borderWidth: 1 },
  statIconWrap: { width: 34, height: 34, borderRadius: 8, justifyContent: "center", alignItems: "center" },
  statValue: { fontSize: 20, fontWeight: "700", letterSpacing: -0.5 },
  statLabel: { fontSize: 11, fontWeight: "500", marginTop: 1, letterSpacing: 0.3 },

  legendRow: { flexDirection: "row", paddingHorizontal: 20, paddingVertical: 10, gap: 8, flexWrap: "wrap" },
  legendPill: { flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 10, paddingVertical: 5, borderRadius: RADIUS.pill, borderWidth: 1 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 11, fontWeight: "500", letterSpacing: 0.3 },

  hScroll: { flex: 1 },
  hScrollContent: { flexGrow: 1 },
  vScroll: { maxHeight: 520 },
  matrixRow: { flexDirection: "row", alignItems: "center" },
  headerRow: { borderBottomWidth: 2, minHeight: HEADER_H },

  cornerCell: { justifyContent: "center", paddingHorizontal: 14 },
  cornerText: { fontSize: 10, fontWeight: "600", letterSpacing: 1 },

  topicHeader: { justifyContent: "center", alignItems: "center", paddingHorizontal: 4, paddingVertical: 6, ...(Platform.OS === "web" ? { cursor: "pointer" } : {}) },
  topicHeaderText: { fontSize: 10, fontWeight: "600", textAlign: "center", lineHeight: 13 },

  nameCell: { justifyContent: "center", paddingHorizontal: 14, paddingVertical: 8, borderRightWidth: 1, minHeight: CELL_H + 2 },
  nameText: { fontSize: 12, fontWeight: "500" },

  cellText: { fontSize: 10, fontWeight: "600" },
  summaryDivider: { borderTopWidth: 2, marginTop: 4 },

  modalOverlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.55)", justifyContent: "center", alignItems: "center", padding: 24 },
  modalCard: { width: "100%", maxWidth: 640, maxHeight: "85%", borderRadius: RADIUS.modal, borderWidth: 1, overflow: "hidden" },
  modalHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1 },
  modalTitle: { fontSize: 16, fontWeight: "700", flex: 1, marginRight: 16 },
  modalCloseBtn: { width: 32, height: 32, borderRadius: 8, justifyContent: "center", alignItems: "center" },
  modalBody: { padding: 24 },

  masteryBarRow: { paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth },
  masteryBarInfo: { marginBottom: 6 },
  masteryBarLabel: { fontSize: 13, fontWeight: "600" },
  masteryBarSub: { fontSize: 11, fontWeight: "500", marginTop: 2 },
  masteryBarTrack: { flexDirection: "row", alignItems: "center", gap: 10 },
  masteryBarTrackBg: { flex: 1, height: 8, borderRadius: 4, overflow: "hidden" },
  masteryBarFill: { height: "100%", borderRadius: 4, minWidth: 2 },

  drillError: { fontSize: 13, fontWeight: "500", textAlign: "center", marginTop: 24 },
  drillEmpty: { fontSize: 13, fontWeight: "500", textAlign: "center", marginTop: 24, fontStyle: "italic" },

  topicBanner: { padding: 16, borderRadius: RADIUS.card, borderWidth: 1, marginBottom: 8 },
  topicBannerLabel: { fontSize: 12, fontWeight: "500" },
  topicBannerValue: { fontSize: 28, fontWeight: "800", letterSpacing: -1 },
  topicMiniBar: { height: 6, borderRadius: 3, overflow: "hidden" },
  topicMiniFill: { height: "100%", borderRadius: 3 },
  missedHeader: { fontSize: 14, fontWeight: "700", marginBottom: 12 },
  missedItem: { paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  missedQ: { fontSize: 13, fontWeight: "500", lineHeight: 18 },
  missedMeta: { fontSize: 11, fontWeight: "500", marginTop: 4 },

  togglePill: {
    flexDirection: "row",
    borderRadius: 8,
    borderWidth: 1,
    overflow: "hidden",
    marginRight: 8,
  },
  toggleOption: {
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  toggleText: {
    fontSize: 11,
  },
});

export default KtMasteryDashboard;
